import type { CouponStock } from '@/api/worldCup';
import { ChartCard } from '../ui/ChartCard';
// Reuses the CouponsByBrandTable layout (generic bar/track/legend classes) so
// the two per-brand panels read as a matched pair on the Rewards tab.
import styles from '../CouponsByBrandTable/style.module.css';

/*
 * Per-brand REMAINING coupon inventory — "how many coupons are left".
 *
 * Visual twin of CouponsByBrandTable: one row per brand, the bar's WIDTH encodes
 * the brand's total pool (vs the largest pool). Inside, a solid segment = codes
 * still AVAILABLE (left in stock), a pale segment = already ISSUED. The
 * right-hand value reads "{left} / {total}" so the headline number is how many
 * remain.
 *
 * Rows arrive sorted by the backend (most remaining first). Brand colour comes
 * from brandColors[brand_id]; a brand with no active pool renders an empty track.
 */
type Props = {
  rows: CouponStock[];
  brandColors: Record<string, string>;
};

const numFmt = new Intl.NumberFormat('en-US');

function v(c?: { value: number }): number {
  return c?.value ?? 0;
}

// Mix a hex/named colour with var(--paper) at paint time. `pct` = how much of
// the brand colour to keep (100 = solid, 0 = paper). Same helper as the sibling.
function tint(brand: string, pct: number): string {
  return `color-mix(in srgb, ${brand} ${pct}%, var(--paper))`;
}

export function CouponStockTable({ rows, brandColors }: Props) {
  if (rows.length === 0) return null;

  // Bar length is proportional to pool size vs the largest pool. Falls back to
  // 1 so an all-zero data set still renders a legible (empty) grid.
  const max = Math.max(1, ...rows.map((r) => v(r.total)));

  return (
    <ChartCard title="Coupons remaining by brand">
      <div className={styles.legend} aria-hidden>
        <span className={styles.legendItem}>
          <span className={styles.legendSwatch} style={{ background: tint('var(--ink)', 100) }} /> Left (available)
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendSwatch} style={{ background: tint('var(--ink)', 22) }} /> Issued
        </span>
      </div>
      <ul className={styles.list}>
        {rows.map((r) => {
          const total = v(r.total);
          const left = v(r.left);
          const issued = v(r.issued);
          const brand = brandColors[r.brand_id] ?? 'var(--ink)';
          const barWidth = `${(total / max) * 100}%`;

          return (
            <li key={r.brand_id} className={styles.row}>
              <span className={`${styles.name} mono`}>{r.name}</span>
              <div className={styles.track} aria-hidden>
                <div className={styles.bar} style={{ width: barWidth }}>
                  <div
                    className={styles.seg}
                    style={{ width: total > 0 ? `${(left / total) * 100}%` : '0%', background: tint(brand, 100) }}
                    title={`Coupons left: ${numFmt.format(left)}`}
                  />
                  <div
                    className={styles.seg}
                    style={{ width: total > 0 ? `${(issued / total) * 100}%` : '0%', background: tint(brand, 22) }}
                    title={`Coupons issued: ${numFmt.format(issued)}`}
                  />
                </div>
              </div>
              <span className={`${styles.value} mono`} title={`${numFmt.format(left)} left of ${numFmt.format(total)}`}>
                {numFmt.format(left)} / {numFmt.format(total)}
              </span>
            </li>
          );
        })}
      </ul>
    </ChartCard>
  );
}
